import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, resolveDynamicComponent as _resolveDynamicComponent } from "vue"

const _hoisted_1 = { class: "flex flex-col h-full" }
const _hoisted_2 = { class: "p-3 border-b space-y-3" }
const _hoisted_3 = { class: "truncate" }
const _hoisted_4 = { class: "truncate" }
const _hoisted_5 = {
  key: 0,
  class: "flex-1 flex items-center justify-center"
}
const _hoisted_6 = { class: "text-center space-y-2" }
const _hoisted_7 = {
  key: 1,
  class: "flex-1 flex items-center justify-center p-4"
}
const _hoisted_8 = { class: "p-2" }
const _hoisted_9 = {
  key: 3,
  class: "border-t p-3 space-y-2"
}
const _hoisted_10 = { class: "flex items-center justify-between" }
const _hoisted_11 = { class: "text-xs text-muted-foreground" }
const _hoisted_12 = { class: "text-xs text-muted-foreground" }
const _hoisted_13 = { class: "text-xs text-muted-foreground truncate" }
const _hoisted_14 = { class: "flex gap-2" }

import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import {
  Search,
  FileText,
  Paperclip,
  Monitor,
  ListTodo,
  CalendarDays,
  ChevronRight,
  CheckSquare,
  XSquare,
  Loader2,
} from 'lucide-vue-next'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useCopilotStore } from '../store'
import { useProjectsStore } from '@/views/Projects/store'
import ContextTreeItem from './ContextTreeItem.vue'
import ProjectSummaryCard from './ProjectSummaryCard.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'ContextPanel',
  setup(__props) {

const copilotStore = useCopilotStore()
const projectsStore = useProjectsStore()

const {
  selectedProjectId,
  projectContext,
  contextItems,
  selectedItemIds,
  isLoadingContext,
  isExtractingContent,
} = storeToRefs(copilotStore)

const {
  projectStats,
  contextSummary,
  availableItemsByType,
  totalSelectedTokenEstimate,
} = storeToRefs(copilotStore)

const projectSearchOpen = ref(false)
const projectSearch = ref('')
const extractingId = ref<string | null>(null)

// Group sections state
const openSections = ref<Record<string, boolean>>({
  document: true,
  attachment: true,
  application: true,
  requirement: true,
  meeting: true,
})

const filteredProjects = computed(() => {
  const search = projectSearch.value.toLowerCase()
  if (!search) return projectsStore.projects
  return projectsStore.projects.filter(
    (p) =>
      p.name.toLowerCase().includes(search) ||
      p.description?.toLowerCase().includes(search)
  )
})

const sections = computed(() => [
  {
    type: 'document',
    label: 'Documentos',
    icon: FileText,
    items: availableItemsByType.value.document || [],
  },
  {
    type: 'attachment',
    label: 'Adjuntos',
    icon: Paperclip,
    items: availableItemsByType.value.attachment || [],
  },
  {
    type: 'application',
    label: 'Aplicaciones',
    icon: Monitor,
    items: availableItemsByType.value.application || [],
  },
  {
    type: 'requirement',
    label: 'Requerimientos',
    icon: ListTodo,
    items: availableItemsByType.value.requirement || [],
  },
  {
    type: 'meeting',
    label: 'Reuniones',
    icon: CalendarDays,
    items: availableItemsByType.value.meeting || [],
  },
])

const handleSelectProject = async (projectId: string) => {
  projectSearchOpen.value = false
  await copilotStore.selectProject(projectId)
}

const handleToggleItem = (itemId: string) => {
  copilotStore.toggleItemSelection(itemId)
}

const handleExtract = async (itemId: string) => {
  const item = contextItems.value.find((i) => i.id === itemId)
  if (!item) return

  // We need the attachment URL and mimeType from the raw data
  // The attachment info is embedded in the item
  extractingId.value = itemId
  try {
    // Fetch attachment data to get URL and mimeType
    const { doc: firestoreDoc, getDoc } = await import('firebase/firestore')
    const { db } = await import('@/config/firebase')
    const attachDoc = await getDoc(firestoreDoc(db, 'attachments', itemId))
    if (attachDoc.exists()) {
      const data = attachDoc.data()
      await copilotStore.extractAttachmentContent(itemId, data.url, data.mimeType)
    }
  } catch (error) {
    console.error('Error extracting content:', error)
  } finally {
    extractingId.value = null
  }
}

const toggleSection = (type: string) => {
  openSections.value[type] = !openSections.value[type]
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(Popover), {
        open: projectSearchOpen.value,
        "onUpdate:open": _cache[1] || (_cache[1] = ($event: any) => ((projectSearchOpen).value = $event))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(PopoverTrigger), { "as-child": "" }, {
            default: _withCtx(() => [
              _createVNode(_unref(Button), {
                variant: "outline",
                role: "combobox",
                "aria-expanded": projectSearchOpen.value,
                class: "w-full justify-between text-sm"
              }, {
                default: _withCtx(() => [
                  _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(projectContext) ? _unref(projectContext).projectName : 'Seleccionar proyecto...'), 1),
                  _createVNode(_unref(ChevronRight), { class: "ml-2 h-4 w-4 shrink-0 opacity-50" })
                ]),
                _: 1
              }, 8, ["aria-expanded"])
            ]),
            _: 1
          }),
          _createVNode(_unref(PopoverContent), {
            class: "w-[280px] p-0 z-[200]",
            align: "start"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Command), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(CommandInput), {
                    modelValue: projectSearch.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((projectSearch).value = $event)),
                    placeholder: "Buscar proyecto..."
                  }, null, 8, ["modelValue"]),
                  _createVNode(_unref(CommandList), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(CommandEmpty), null, {
                        default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                          _createTextVNode("No se encontraron proyectos.", -1)
                        ]))]),
                        _: 1
                      }),
                      _createVNode(_unref(CommandGroup), null, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredProjects.value, (project) => {
                            return (_openBlock(), _createBlock(_unref(CommandItem), {
                              key: project.id,
                              value: project.name,
                              onSelect: ($event: any) => (handleSelectProject(project.id))
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("span", _hoisted_4, _toDisplayString(project.name), 1)
                              ]),
                              _: 2
                            }, 1032, ["value", "onSelect"]))
                          }), 128))
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["open"]),
      (_unref(projectContext))
        ? (_openBlock(), _createBlock(ProjectSummaryCard, {
            key: 0,
            "project-name": _unref(projectContext).projectName,
            "project-status": _unref(projectContext).projectStatus,
            stats: _unref(projectStats)
          }, null, 8, ["project-name", "project-status", "stats"]))
        : _createCommentVNode("", true)
    ]),
    (_unref(isLoadingContext))
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_unref(Loader2), { class: "h-6 w-6 animate-spin mx-auto text-muted-foreground" }),
            _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-sm text-muted-foreground" }, "Cargando contexto...", -1))
          ])
        ]))
      : (!_unref(projectContext))
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [...(_cache[6] || (_cache[6] = [
            _createElementVNode("p", { class: "text-sm text-muted-foreground text-center" }, " Selecciona un proyecto para cargar su contexto ", -1)
          ]))]))
        : (_openBlock(), _createBlock(_unref(ScrollArea), {
            key: 2,
            class: "flex-1"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_8, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sections.value, (section) => {
                  return (_openBlock(), _createElementBlock(_Fragment, {
                    key: section.type
                  }, [
                    (section.items.length > 0)
                      ? (_openBlock(), _createBlock(_unref(Collapsible), {
                          key: 0,
                          open: openSections.value[section.type],
                          class: "mb-1"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(CollapsibleTrigger), {
                              class: "flex items-center gap-2 w-full px-2 py-1.5 text-sm font-medium hover:bg-muted/50 rounded-md",
                              onClick: ($event: any) => (toggleSection(section.type))
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(ChevronRight), {
                                  class: _normalizeClass(["h-4 w-4 transition-transform", { 'rotate-90': openSections.value[section.type] }])
                                }, null, 8, ["class"]),
                                (_openBlock(), _createBlock(_resolveDynamicComponent(section.icon), { class: "h-4 w-4 text-muted-foreground" })),
                                _createElementVNode("span", null, _toDisplayString(section.label), 1),
                                _createVNode(_unref(Badge), {
                                  variant: "secondary",
                                  class: "ml-auto text-[10px] px-1.5 py-0 h-4"
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(section.items.length), 1)
                                  ]),
                                  _: 2
                                }, 1024)
                              ]),
                              _: 2
                            }, 1032, ["onClick"]),
                            _createVNode(_unref(CollapsibleContent), { class: "pl-4" }, {
                              default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(section.items, (item) => {
                                  return (_openBlock(), _createBlock(ContextTreeItem, {
                                    key: item.id,
                                    item: item,
                                    selected: _unref(selectedItemIds).includes(item.id),
                                    "is-extracting": extractingId.value === item.id,
                                    onToggle: handleToggleItem,
                                    onExtract: handleExtract
                                  }, null, 8, ["item", "selected", "is-extracting"]))
                                }), 128))
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          _: 2
                        }, 1032, ["open"]))
                      : _createCommentVNode("", true)
                  ], 64))
                }), 128))
              ])
            ]),
            _: 1
          })),
    (_unref(projectContext))
      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("p", _hoisted_11, _toDisplayString(_unref(selectedItemIds).length) + " items seleccionados ", 1),
            _createElementVNode("p", _hoisted_12, " ~" + _toDisplayString(_unref(totalSelectedTokenEstimate).toLocaleString()) + " tokens ", 1)
          ]),
          _createElementVNode("p", _hoisted_13, _toDisplayString(_unref(contextSummary)), 1),
          _createElementVNode("div", _hoisted_14, [
            _createVNode(_unref(Button), {
              variant: "outline",
              size: "sm",
              class: "flex-1 text-xs h-7",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(copilotStore).selectAllItems()))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(CheckSquare), { class: "h-3 w-3 mr-1" }),
                _cache[7] || (_cache[7] = _createTextVNode(" Todo ", -1))
              ]),
              _: 1
            }),
            _createVNode(_unref(Button), {
              variant: "outline",
              size: "sm",
              class: "flex-1 text-xs h-7",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(copilotStore).deselectAllItems()))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(XSquare), { class: "h-3 w-3 mr-1" }),
                _cache[8] || (_cache[8] = _createTextVNode(" Limpiar ", -1))
              ]),
              _: 1
            })
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})